import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../utils/config';
import { useTranslation } from '../context/TranslationContext';
import '../styles/NotificationModal.css';

interface RevisionNotification {
  _id: string;
  product_name: string;
  revision_notes: string;
  revision_requested_at: string;
  revision_requested_by: string;
  requester_name: string;
  company_name: string;
}

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRevisionClick: (requestId: string) => void;
}

const NotificationModal: React.FC<NotificationModalProps> = ({ isOpen, onClose, onRevisionClick }) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<RevisionNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setError(t('common.authentication_required'));
        return;
      }

      const response = await fetch(API_ENDPOINTS.REVISION_NOTIFICATIONS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setNotifications(data.data || []);
      } else {
        setError(data.message || t('common.failed_fetch_notifications'));
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(t('common.failed_fetch_notifications'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleNotificationClick = (notification: RevisionNotification) => {
    // Call the parent handler to open revision modal
    onRevisionClick(notification._id);
    onClose(); // Close notification modal
  };

  if (!isOpen) return null;

  return (
    <div className="notification-modal-overlay" onClick={onClose}>
      <div className="notification-modal" onClick={(e) => e.stopPropagation()}>
        <div className="notification-modal-header">
          <h2>{t('common.notifications')}</h2>
          <button className="notification-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="notification-modal-content">
          {loading ? (
            <div className="notification-loading">
              <div className="loading-spinner"></div>
              <p>{t('common.loading_notifications')}</p>
            </div>
          ) : error ? (
            <div className="notification-error">
              <p>{error}</p>
              <button onClick={fetchNotifications} className="retry-btn">
                {t('common.retry')}
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="notification-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 17H20L18.5951 15.5951C18.2141 15.2141 18 14.6973 18 14.1585V11C18 8.38757 16.3304 6.16509 14 5.34142V5C14 3.89543 13.1046 3 12 3C10.8954 3 10 3.89543 10 5V5.34142C7.66962 6.16509 6 8.38757 6 11V14.1585C6 14.6973 5.78595 15.2141 5.40493 15.5951L4 17H9M15 17V18C15 19.6569 13.6569 21 12 21C10.3431 21 9 19.6569 9 18V17M15 17H9" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p>{t('common.no_notifications_yet')}</p>
              <span>{t('common.all_updates_here')}</span>
            </div>
          ) : (
            <div className="notification-list">
              {notifications.map((notification) => (
                <div 
                  key={notification._id} 
                  className="notification-item"
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">
                      {t('common.revision_required', { product_name: notification.product_name })}
                    </div>
                    <div className="notification-message">
                      {notification.revision_notes}
                    </div>
                    <div className="notification-meta">
                      <span className="notification-time">
                        {formatDate(notification.revision_requested_at)}
                      </span>
                      <span className="notification-requester">
                        {notification.requester_name || t('common.unknown_user')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationModal; 